<template>
  <div class="featured-clubs">
    <div class="grid__row">
      <div class="grid__column grid__column--12">
        <HeaderContent type="primary" direction="row">
          <template #primary-left>
            <h2 class="featured-clubs__heading heading--h3">Featured Venues</h2>
          </template>
          <template #primary-right>
            <router-link :to="coursesRouteNames.Courses">
              <USButton type="inline" label="View All Courses" />
            </router-link>
            <br />
            <router-link :to="clubsRouteNames.IndoorGolf">
              <USButton type="inline" label="View All Indoor Golf" />
            </router-link>
          </template>
        </HeaderContent>
      </div>
    </div>
    <CardList :cards="cards" />
  </div>
</template>

<script lang="ts" setup>
import { ClubSortField, SortOrder } from '__generated__/globalTypes'
import {
  featuredClubs,
  featuredClubs_listClubs_ListClubsOutput_clubs
} from '__generated__/featuredClubs'

import { defineComponent, ref } from 'vue'

import {
  Card,
  CardListingClubDetails,
  CardListingCourse,
  CardType
} from '@base/_types/Card'

import { useClient } from '@base/api/useClient'

import { ugcImageSrc } from '@base/lib/ugcImageSrc'

import CardList from '@base/components/CardList/CardList.vue'
import HeaderContent from '@base/components/HeaderContent/HeaderContent.vue'
import USButton from '@base/components/USButton/USButton.vue'

import { createSlug } from '@base/lib/createSlug'

import { formatCoursePriceRange } from '@base/helpers/formatCoursePriceRange'

import { coursesRouteNames } from '@player/courses/router/routeNames'
import { FEATURED_CLUBS } from '@base/api/queries/featuredClubs'
import { clubsRouteNames } from '@player/clubs/router/routeNames'

import imageEventPlaceholder from '@player/_assets/event-placeholder.jpg'

defineComponent({
  name: 'FeaturedClubs',
  components: {
    CardList,
    HeaderContent,
    USButton
  }
})

const client = useClient()
const cards = ref<Card[]>([])

const generateClubLogo = (logo: string, clubName: string) => {
  return {
    src: logo ? ugcImageSrc(logo)[0] : '',
    srcset: logo ? ugcImageSrc(logo)[0] : '',
    alt: `The logo for ${clubName}`
  }
}

const buildCardForClub = (
  club: featuredClubs_listClubs_ListClubsOutput_clubs
): Card | null => {
  const clubDetails: CardListingClubDetails = {
    name: club.clubName,
    rating: club.averageRating ?? 0,
    logo: generateClubLogo(club.logo || '', club.clubName),
    to: {
      name: clubsRouteNames.ClubSingle,
      params: {
        slug: createSlug(club.clubName, club.slug)
      }
    }
  }

  const coursesWrapper = club.courses
  if (coursesWrapper.__typename !== 'ListCoursesOutput') {
    return null
  }

  const courseRows = coursesWrapper.courses

  if (courseRows.length === 0) {
    const data: CardListingCourse = {
      club: clubDetails,
      course: {
        name: club.clubName,
        location: club.city,
        distance: '',
        holes: 0,
        par: 0,
        price: '',
        featuredImage: {
          src:
            club.gallery && club.gallery[0]
              ? ugcImageSrc(club.gallery[0] || '')[0]
              : imageEventPlaceholder
        },
        to: {
          teeTimes: {
            name: clubsRouteNames.ClubSingle,
            params: {
              slug: createSlug(club.clubName, club.slug)
            }
          }
        }
      }
    }
    return { type: CardType.CardListingCourse, data }
  }

  const course = courseRows[0]

  const data: CardListingCourse = {
    club: clubDetails,
    course: {
      name: course.courseName,
      location: club.city,
      distance: '',
      holes: course.numHoles ?? 0,
      par: course.par ?? 0,
      price: formatCoursePriceRange(course.priceRange || ''),
      featuredImage: {
        src:
          course.gallery && course.gallery[0]
            ? ugcImageSrc(course.gallery[0] || '')[0]
            : imageEventPlaceholder
      },
      to: {
        teeTimes: {
          name: coursesRouteNames.CourseSingle,
          params: {
            slug: createSlug(course.courseName, course.slug)
          }
        }
      }
    }
  }
  return { type: CardType.CardListingCourse, data }
}

const fetchRandomClubs = async () => {
  try {
    const { data } = await client.query<featuredClubs>({
      query: FEATURED_CLUBS,
      variables: {
        filter: {},
        sortField: ClubSortField.NAME,
        sortOrder: SortOrder.ASC,
        limit: 3,
        offset: 0,
        randomize: true
      },
      fetchPolicy: 'cache-first'
    })

    if (!data || data.listClubs.__typename !== 'ListClubsOutput') {
      return
    }

    const nextCards: Card[] = []
    for (const club of data.listClubs.clubs) {
      const card = buildCardForClub(club)
      if (card) {
        nextCards.push(card)
      }
    }
    cards.value = nextCards
  } catch (e) {
    console.error(e)
  }
}

await fetchRandomClubs()
</script>

<style lang="scss" scoped>
.featured-clubs {
  margin-bottom: var(--spacing--template);
  margin-top: var(--spacing--template);
}
</style>
